import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator, Image, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text, TextInput } from '@/components/common/AppText';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { colors, motion } from '@tabbio/tokens';
import { evaluatePassword, passwordSchema } from '@tabbio/utils';
import { PasswordField } from '../common/PasswordField';
import { authClient, signIn, signUp } from '../../lib/auth-client';
import { sendSignupOtp, verifySignupOtp } from '../../lib/auth-api';
import { getAuthErrorCopy } from '../../lib/auth-error-copy';
import { buildRedirectURL } from '../../lib/deep-link';
import {
  commitPostAuthRedirect,
  getPostAuthRedirectHref,
  savePostAuthTarget,
  type PostAuthTarget,
} from '../../lib/auth-handoff';
import { readResumeHandoffTokenAsync } from '../../lib/resume-handoff';
import { trackProductEvent } from '../../lib/product-analytics';
import { useTheme } from '../../hooks/useTheme';
import { useResendCooldown } from '../../hooks/useResendCooldown';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';
import { NativeButton } from '../common/NativeButton';
import { PasswordBootstrapForm } from './PasswordBootstrapForm';

/** Matches WebDialog's sheet mode and components/onboarding/BottomSheet.web. */
const SHEET_MAX_WIDTH = 480;
const SHEET_CORNER_RADIUS = 55;

type Step =
  | 'password'
  | 'otp'
  | 'signup-otp'
  | 'signup'
  | 'forgot-password'
  | 'reset-password'
  | 'bootstrap';

function sanitizeUsername(v: string) {
  return v.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 30);
}


type ThemeShape = {
  text: string;
  textTertiary: string;
  border: string;
  background: string;
  focusRing: string;
};

type InputFieldProps = React.ComponentProps<typeof TextInput> & {
  hasError?: boolean;
  theme: ThemeShape;
};

// Hoisted to module scope so the underlying <input> (web) is not remounted
// on every parent re-render: defining this inside AuthStepSheet caused the
// focus to be lost on every keystroke on web.
function InputField(props: InputFieldProps) {
  const { hasError, style: extraStyle, ...rest } = props;
  const [focused, setFocused] = useState(false);
  return (
    <TextInput
      {...rest}
      onFocus={(e) => { setFocused(true); rest.onFocus?.(e); }}
      onBlur={(e) => { setFocused(false); rest.onBlur?.(e); }}
      style={[
        styles.input,
        {
          color: 'var(--text-primary)',
          borderColor: hasError
            ? '#d93025'
            : focused
              ? 'var(--text-primary)'
              : 'var(--input-border)',
          backgroundColor: 'var(--input-bg)',
          outlineStyle: 'none',
          transitionProperty: 'border-color',
          transitionDuration: `${motion.duration.fast}ms`,
          boxShadow: 'var(--gem-input-shadow)',
        } as any,
        extraStyle,
      ]}
    />
  );
}

// Gemini-style primary CTA: solid neutral pill that adapts to theme.
function PrimaryCta({
  title,
  onPress,
  loading,
  disabled,
}: {
  title: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
}) {
  const isDisabled = disabled || loading;
  return (
    <Pressable
      accessibilityRole="button"
      onPress={isDisabled ? undefined : onPress}
      disabled={isDisabled}
      style={({ hovered }: any) => [
        {
          width: '100%',
          height: 48,
          borderRadius: 9999,
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'row',
          gap: 8,
          backgroundColor: 'var(--text-primary)',
          opacity: isDisabled ? 0.4 : hovered ? 0.88 : 1,
          cursor: isDisabled ? 'default' : 'pointer',
          transitionProperty: 'opacity',
          transitionDuration: `${motion.duration.fast}ms`,
        } as any,
      ]}
    >
      {loading ? <ActivityIndicator size="small" color="var(--bg)" /> : null}
      <Text
        style={{
          color: 'var(--bg)',
          fontSize: 15,
          fontWeight: '500',
        }}
      >
        {title}
      </Text>
    </Pressable>
  );
}

/**
 * Self-contained auth step dialog for web.
 * Receives initial params via `onClose` + extra props from DialogProvider.
 */
export function AuthStepSheet({
  onClose,
  email: initialEmail,
  step: initialStep,
  hasPassword: initialHasPassword,
  partnerNotice = false,
  postAuthTarget = 'root',
}: {
  onClose: () => void;
  email?: string;
  step?: string;
  hasPassword?: boolean;
  /** Set by LoginContent when email-status reported an active partner account. */
  partnerNotice?: boolean;
  postAuthTarget?: PostAuthTarget;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation('auth');
  const router = useRouter();
  // Phone widths present this as a bottom sheet instead of a centred card, so
  // it matches every other modal surface on web (see WebDialog.web.tsx).
  const isSheet = useIsMobileViewport();

  const email = (initialEmail as string) || '';
  // Mirrors auth-step.tsx (native): a pending resume handoff means the user
  // arrived from a public funnel expecting to CREATE an account. When their
  // email already has one, the silent switch to sign-in reads as a bug — an
  // explicit notice names what happened and their options.
  const [hasPendingCvImport, setHasPendingCvImport] = useState(false);
  useEffect(() => {
    void readResumeHandoffTokenAsync().then((token) => {
      setHasPendingCvImport(Boolean(token));
    });
  }, []);
  const startStep: Step =
    initialStep === 'signup-otp'
      ? 'signup-otp'
      : initialStep === 'signup'
        ? 'signup'
        : initialStep === 'otp'
          ? 'otp'
          : initialStep === 'bootstrap'
            ? 'bootstrap'
            : 'password';
  const requiresPasswordBootstrap =
    initialStep === 'bootstrap' ||
    (initialStep === 'otp' && initialHasPassword === false);

  const [step, setStep] = useState<Step>(startStep);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const otpResendCooldown = useResendCooldown();
  const resetLinkCooldown = useResendCooldown();
  const initialOtpRequestStartedRef = useRef(false);
  const otpRequestInFlightRef = useRef(false);

  // sign-in
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');

  // signup
  const [sName, setSName] = useState('');
  const [sUsername, setSUsername] = useState('');
  const [sPassword, setSPassword] = useState('');
  const [signupOtp, setSignupOtp] = useState('');
  const [signupVerifyAttempted, setSignupVerifyAttempted] = useState(false);
  const [avatarUri, setAvatarUri] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearErr = () => { if (error) setError(''); };

  const routeAfterAuth = () => {
    onClose();
    commitPostAuthRedirect(router, postAuthTarget);
  };

  // send OTP on mount if OTP step
  useEffect(() => {
    if (initialOtpRequestStartedRef.current) return;
    initialOtpRequestStartedRef.current = true;
    if (startStep === 'otp') void sendOtpCode();
    if (startStep === 'signup-otp') void sendSignupOtpCode();
    // Funnel step 3. The caller resolves the email's status before opening
    // this sheet, so these two start steps are exactly the cases where the
    // address had no account and the visitor is being asked to create one.
    // Measuring from here rather than from the password screen keeps the
    // email-verification drop-off inside the funnel instead of hiding it
    // upstream of step 3.
    if (startStep === 'signup-otp' || startStep === 'signup') {
      // No `surface` here: the analytics sink resolves it per platform
      // (`app_web` on web), and passing one from a call site overrides it. This
      // file is the web dialog, but its native sibling shares the same route.
      trackProductEvent('signup_started', { signup_method: 'email' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendOtpCode = async () => {
    if (otpRequestInFlightRef.current) return;
    otpRequestInFlightRef.current = true;
    setError('');
    setSendingCode(true);
    try {
      await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' });
      setOtp('');
      otpResendCooldown.start();
    } catch (err) {
      setError(getAuthErrorCopy(err, t, t('login.otpSendError')));
    } finally {
      otpRequestInFlightRef.current = false;
      setSendingCode(false);
    }
  };

  const sendSignupOtpCode = async () => {
    if (otpRequestInFlightRef.current) return;
    otpRequestInFlightRef.current = true;
    setError('');
    setSendingCode(true);
    try {
      await sendSignupOtp(email);
      setSignupOtp('');
      setSignupVerifyAttempted(false);
      otpResendCooldown.start();
    } catch (err) {
      setError(getAuthErrorCopy(err, t, t('login.otpSendError')));
    } finally {
      otpRequestInFlightRef.current = false;
      setSendingCode(false);
    }
  };

  const handleVerifySignupOtp = async () => {
    setSignupVerifyAttempted(true);
    if (signupOtp.trim().length < 6) { setError(t('login.otpRequired')); return; }
    setSubmitting(true);
    setError('');
    try {
      await verifySignupOtp(email, signupOtp.trim());
      setSignupOtp('');
      setError('');
      setSignupVerifyAttempted(false);
      setStep('signup');
    } catch (err) {
      setError(getAuthErrorCopy(err, t, t('login.otpSignInError')));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Handlers ──────────────────────────────────────────────────────

  const handlePasswordSignIn = async () => {
    if (!password.trim()) { setError(t('login.passwordRequired')); return; }
    setSubmitting(true); setError('');
    try {
      const { error: resError } = await signIn.email({ email, password });
      if (resError) {
        setError(resError.message || t('login.passwordSignInError'));
      } else {
        routeAfterAuth();
      }
    } catch (err) {
      setError(getAuthErrorCopy(err, t, t('login.passwordSignInError')));
    } finally { setSubmitting(false); }
  };

  const handleOtpSignIn = async () => {
    if (otp.trim().length < 6) { setError(t('login.otpRequired')); return; }
    setSubmitting(true); setError('');
    try {
      const { error: resError } = await signIn.emailOtp({ email, otp: otp.trim() });
      if (resError) {
        setError(resError.message || t('login.otpSignInError'));
      } else {
        if (requiresPasswordBootstrap) {
          setOtp('');
          setError('');
          savePostAuthTarget(
            postAuthTarget === 'password-bootstrap' ? 'root' : postAuthTarget,
          );
          onClose();
          router.replace(getPostAuthRedirectHref('password-bootstrap'));
          return;
        }
        routeAfterAuth();
      }
    } catch (err) {
      setError(getAuthErrorCopy(err, t, t('login.otpSignInError')));
    } finally { setSubmitting(false); }
  };

  const handleChooseOtp = async () => {
    setError(''); setOtp(''); setStep('otp');
    try { await sendOtpCode(); } catch { /* set in sendOtpCode */ }
  };

  // ─── Forgot / Reset Password ─────────────────────────────────────

  const [resetSent, setResetSent] = useState(false);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleRequestReset = async () => {
    setSubmitting(true); setError('');
    try {
      const { error: resError } = await authClient.requestPasswordReset({
        email,
        redirectTo: buildRedirectURL('/(auth)/reset-password'),
      });
      if (resError) {
        setError(resError.message || t('forgotPassword.sendError'));
      } else {
        setResetSent(true);
        resetLinkCooldown.start();
      }
    } catch (err) {
      setError(getAuthErrorCopy(err, t, t('forgotPassword.sendError')));
    } finally { setSubmitting(false); }
  };

  const handleResetPassword = async () => {
    const pp = passwordSchema.safeParse(resetNewPassword);
    if (!pp.success) { setError(pp.error.errors[0]?.message || t('login.passwordValidationError')); return; }
    if (resetNewPassword !== resetConfirmPassword) { setError(t('resetPassword.mismatch')); return; }
    if (!resetToken.trim()) { setError(t('resetPassword.invalidToken')); return; }
    setSubmitting(true); setError('');
    try {
      const { error: resError } = await authClient.resetPassword({ newPassword: resetNewPassword, token: resetToken });
      if (resError) {
        setError(resError.message || t('resetPassword.error'));
      } else {
        setResetSuccess(true);
      }
    } catch (err) {
      setError(getAuthErrorCopy(err, t, t('resetPassword.error')));
    } finally { setSubmitting(false); }
  };

  const handleSignUp = async () => {
    const pp = passwordSchema.safeParse(sPassword);
    if (!pp.success) { setError(pp.error.errors[0]?.message || t('login.passwordValidationError')); return; }
    setSubmitting(true); setError('');
    try {
      const { error: resError } = await signUp.email({ email, name: email.split('@')[0], password: sPassword });
      if (resError) {
        trackProductEvent('signup_failed', { signup_method: 'email', error_kind: 'api' });
        setError(resError.message || t('login.signupError'));
      } else {
        // Funnel step 4. Inside the success branch: Better Auth reports a
        // rejected signup through `resError` rather than by throwing, so a
        // call placed after the try/catch would count failures as conversions.
        trackProductEvent('signup_completed', { signup_method: 'email' });
        routeAfterAuth();
      }
    } catch (err) {
      trackProductEvent('signup_failed', { signup_method: 'email', error_kind: 'network' });
      setError(getAuthErrorCopy(err, t, t('login.signupError')));
    } finally { setSubmitting(false); }
  };

  const pickAvatar = () => fileInputRef.current?.click();
  const handleFileChange = (e: any) => {
    const file = e.target?.files?.[0];
    if (file) setAvatarUri(URL.createObjectURL(file));
  };

  const canDismiss = step !== 'bootstrap';

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <View style={[styles.overlay, isSheet && styles.overlaySheet]}>
      <Pressable
        style={styles.backdrop}
        onPress={canDismiss ? onClose : undefined}
      />
      <View
        style={[styles.sheetWrap, isSheet && styles.sheetWrapSheet]}
        pointerEvents="box-none"
      >
        <ScrollView
          bounces={false}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={[
            styles.scrollContent,
            isSheet && styles.scrollContentSheet,
          ]}
          style={styles.scrollFill}
          showsVerticalScrollIndicator={false}
        >
          {/* `dataSet` is how an RN view opts into the shared sheet hairline
              that WebDialog gets via className. It is a react-native-web prop,
              absent from the upstream View types. */}
          <View
            style={[styles.sheet, isSheet && styles.sheetMobile]}
            {...({
              dataSet: isSheet ? { tabbioSheet: 'true' } : undefined,
            } as object)}
          >
            {/* Grab handle: sheet-only affordance, matches BottomSheet.web */}
            {isSheet ? <View style={styles.handle} /> : null}

            {/* Header */}
            {canDismiss ? (
              <View style={styles.headerRow}>
                <NativeButton title={t('login.back')} onPress={onClose} variant="ghost" size="sm" />
                <NativeButton title={t('login.useAnotherEmail')} onPress={onClose} variant="ghost" size="sm" />
              </View>
            ) : null}

            {/* Non-blocking partner notice: the sheet covers the login screen,
                so the notice has to be repeated here to be readable. */}
            {partnerNotice ? (
              <Text style={[styles.notice, { color: 'var(--text-secondary)' }]}>
                {t('login.partnerAccountNotice')}
              </Text>
            ) : null}

            {/* ── Signup OTP ── */}
            {step === 'signup-otp' ? (
              <View style={styles.section}>
                <Text style={[styles.title, { color: 'var(--text-primary)' }]}>
                  {t('login.signupOtpTitle', { defaultValue: 'Verify your email' })}
                </Text>
                <Text style={[styles.subtitle, { color: 'var(--text-secondary)' }]}>
                  {t('login.signupOtpSubtitle', {
                    defaultValue: 'Enter the 6-digit code we sent to {{email}}.',
                    email,
                  })}
                </Text>

                <InputField
                  theme={theme}
                  placeholder={t('login.otpPlaceholder')}
                  placeholderTextColor={theme.textTertiary}
                  value={signupOtp}
                  onChangeText={(v) => {
                    setSignupOtp(v);
                    setSignupVerifyAttempted(false);
                    clearErr();
                  }}
                  hasError={signupVerifyAttempted && !!error}
                  autoCapitalize="none"
                  autoComplete="one-time-code"
                  autoFocus
                  returnKeyType="go"
                  onSubmitEditing={handleVerifySignupOtp}
                  maxLength={6}
                  style={styles.codeInput}
                />

                {error ? <Text style={[styles.error, { color: '#d93025' }]}>{error}</Text> : null}

                <PrimaryCta title={t('login.verifyCode')} onPress={handleVerifySignupOtp} loading={submitting} disabled={signupOtp.trim().length < 6} />
                <NativeButton
                  title={
                    otpResendCooldown.isActive
                      ? t('login.resendCodeIn', { seconds: otpResendCooldown.secondsLeft })
                      : t('login.resendCode')
                  }
                  onPress={() => { void sendSignupOtpCode(); }}
                  variant="ghost" size="md" block
                  loading={sendingCode}
                  disabled={otpResendCooldown.isActive || sendingCode}
                />
              </View>

            /* ── Signup ── */
            ) : step === 'signup' ? (
              <View style={styles.section}>
                <Text style={[styles.title, { color: 'var(--text-primary)' }]}>
                  {t('login.signupTitle')}
                </Text>
                <Text style={[styles.subtitle, { color: 'var(--text-secondary)' }]}>
                  {t('login.signupSubtitle', { email })}
                </Text>

                <View style={styles.form}>
                  <PasswordField
                    placeholder={t('register.passwordPlaceholder')}
                    placeholderTextColor={theme.textTertiary}
                    value={sPassword}
                    onChangeText={(v) => { setSPassword(v); clearErr(); }}
                    showRules
                    autoComplete="new-password"
                    textContentType="newPassword"
                    autoFocus
                    returnKeyType="go"
                    onSubmitEditing={handleSignUp}
                  />
                </View>

                {error ? <Text style={[styles.error, { color: '#d93025' }]}>{error}</Text> : null}

                <PrimaryCta title={t('login.createAccount')} onPress={handleSignUp} loading={submitting} disabled={!evaluatePassword(sPassword).valid} />
              </View>

            /* ── Password ── */
            ) : step === 'password' ? (
              <View style={styles.section}>
                <Text style={[styles.title, { color: 'var(--text-primary)' }]}>
                  {t('login.passwordTitle')}
                </Text>
                <Text style={[styles.subtitle, { color: 'var(--text-secondary)' }]}>
                  {t('login.passwordSubtitle')}
                </Text>

                {hasPendingCvImport ? (
                  <View
                    style={[
                      styles.accountFoundNotice,
                      { backgroundColor: theme.surfaceSecondary },
                    ]}
                  >
                    <Text style={[styles.accountFoundTitle, { color: 'var(--text-primary)' }]}>
                      {t('login.accountFoundTitle', {
                        defaultValue: 'We found your Tabbio account',
                      })}
                    </Text>
                    <Text style={[styles.accountFoundBody, { color: 'var(--text-secondary)' }]}>
                      {t('login.accountFoundBody', {
                        defaultValue:
                          'This email already has an account. Sign in and your imported CV will be added to it. Want a separate account? Choose "Use another email" above.',
                      })}
                    </Text>
                  </View>
                ) : null}

                <PasswordField
                  placeholder={t('login.passwordPlaceholder')}
                  placeholderTextColor={theme.textTertiary}
                  value={password}
                  onChangeText={(v) => { setPassword(v); clearErr(); }}
                  error={!!error}
                  autoComplete="password"
                  textContentType="password"
                  autoFocus
                  returnKeyType="go"
                  onSubmitEditing={handlePasswordSignIn}
                />

                {error ? <Text style={[styles.error, { color: '#d93025' }]}>{error}</Text> : null}

                <PrimaryCta title={t('login.signIn')} onPress={handlePasswordSignIn} loading={submitting} disabled={!password.trim()} />
                <NativeButton
                  title={t('forgotPassword.link')}
                  onPress={() => { setError(''); setResetSent(false); setStep('forgot-password'); }}
                  variant="ghost" size="md" block
                />
                <NativeButton
                  title={t('login.useEmailCode')}
                  onPress={() => { void handleChooseOtp(); }}
                  variant="ghost" size="md" block
                />
              </View>

            /* ── Forgot Password ── */
            ) : step === 'forgot-password' ? (
              <View style={styles.section}>
                {resetSent ? (
                  <>
                    <Text style={[styles.title, { color: 'var(--text-primary)' }]}>
                      {t('forgotPassword.sentTitle')}
                    </Text>
                    <Text style={[styles.subtitle, { color: 'var(--text-secondary)' }]}>
                      {t('forgotPassword.sentSubtitle', { email })}
                    </Text>
                    <NativeButton
                      title={
                        resetLinkCooldown.isActive
                          ? t('forgotPassword.resendIn', { seconds: resetLinkCooldown.secondsLeft })
                          : t('forgotPassword.resend')
                      }
                      onPress={() => { void handleRequestReset(); }}
                      variant="ghost" size="md" block
                      loading={submitting}
                      disabled={resetLinkCooldown.isActive}
                    />
                    <NativeButton
                      title={t('login.back')}
                      onPress={() => { setError(''); setStep('password'); }}
                      variant="ghost" size="md" block
                    />
                  </>
                ) : (
                  <>
                    <Text style={[styles.title, { color: 'var(--text-primary)' }]}>
                      {t('forgotPassword.title')}
                    </Text>
                    <Text style={[styles.subtitle, { color: 'var(--text-secondary)' }]}>
                      {t('forgotPassword.subtitle')}
                    </Text>
                    <InputField
                      theme={theme}
                      value={email}
                      editable={false}
                      style={{ opacity: 0.6 } as any}
                    />
                    {error ? <Text style={[styles.error, { color: '#d93025' }]}>{error}</Text> : null}
                    <PrimaryCta
                      title={t('forgotPassword.sendLink')}
                      onPress={() => { void handleRequestReset(); }}
                      loading={submitting}
                    />
                    <NativeButton
                      title={t('login.back')}
                      onPress={() => { setError(''); setStep('password'); }}
                      variant="ghost" size="md" block
                    />
                  </>
                )}
              </View>

            /* ── Reset Password ── */
            ) : step === 'reset-password' ? (
              <View style={styles.section}>
                {resetSuccess ? (
                  <>
                    <Text style={[styles.title, { color: 'var(--text-primary)' }]}>
                      {t('resetPassword.successTitle')}
                    </Text>
                    <Text style={[styles.subtitle, { color: 'var(--text-secondary)' }]}>
                      {t('resetPassword.successSubtitle')}
                    </Text>
                    <PrimaryCta
                      title={t('resetPassword.backToLogin')}
                      onPress={() => { setError(''); setStep('password'); setPassword(''); }}
                    />
                  </>
                ) : (
                  <>
                    <Text style={[styles.title, { color: 'var(--text-primary)' }]}>
                      {t('resetPassword.title')}
                    </Text>
                    <Text style={[styles.subtitle, { color: 'var(--text-secondary)' }]}>
                      {t('resetPassword.subtitle')}
                    </Text>
                    <PasswordField
                      placeholder={t('resetPassword.newPasswordPlaceholder')}
                      placeholderTextColor={theme.textTertiary}
                      value={resetNewPassword}
                      onChangeText={(v) => { setResetNewPassword(v); clearErr(); }}
                      error={!!error}
                      showRules
                      autoComplete="new-password"
                      textContentType="newPassword"
                      autoFocus
                      returnKeyType="next"
                    />
                    <PasswordField
                      placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                      placeholderTextColor={theme.textTertiary}
                      value={resetConfirmPassword}
                      onChangeText={(v) => { setResetConfirmPassword(v); clearErr(); }}
                      error={!!error}
                      autoComplete="new-password"
                      textContentType="newPassword"
                      returnKeyType="go"
                      onSubmitEditing={() => { void handleResetPassword(); }}
                    />
                    {error ? <Text style={[styles.error, { color: '#d93025' }]}>{error}</Text> : null}
                    <PrimaryCta
                      title={t('resetPassword.reset')}
                      onPress={() => { void handleResetPassword(); }}
                      loading={submitting}
                      disabled={!evaluatePassword(resetNewPassword).valid || !resetConfirmPassword.trim()}
                    />
                  </>
                )}
              </View>

            /* ── Bootstrap ── */
            ) : step === 'bootstrap' ? (
              <PasswordBootstrapForm
                email={email}
                onComplete={routeAfterAuth}
              />

            /* ── OTP ── */
            ) : (
              <View style={styles.section}>
                <Text style={[styles.title, { color: 'var(--text-primary)' }]}>
                  {t('login.otpTitle')}
                </Text>
                <Text style={[styles.subtitle, { color: 'var(--text-secondary)' }]}>
                  {t('login.otpSubtitle', { email })}
                </Text>

                {hasPendingCvImport ? (
                  <View
                    style={[
                      styles.accountFoundNotice,
                      { backgroundColor: theme.surfaceSecondary },
                    ]}
                  >
                    <Text style={[styles.accountFoundTitle, { color: 'var(--text-primary)' }]}>
                      {t('login.accountFoundTitle', {
                        defaultValue: 'We found your Tabbio account',
                      })}
                    </Text>
                    <Text style={[styles.accountFoundBody, { color: 'var(--text-secondary)' }]}>
                      {t('login.accountFoundBody', {
                        defaultValue:
                          'This email already has an account. Sign in and your imported CV will be added to it. Want a separate account? Choose "Use another email" above.',
                      })}
                    </Text>
                  </View>
                ) : null}

                <InputField
                  theme={theme}
                  placeholder={t('login.otpPlaceholder')}
                  placeholderTextColor={theme.textTertiary}
                  value={otp}
                  onChangeText={(v) => { setOtp(v); clearErr(); }}
                  hasError={!!error}
                  autoCapitalize="none"
                  autoComplete="one-time-code"
                  autoFocus
                  returnKeyType="go"
                  onSubmitEditing={handleOtpSignIn}
                  maxLength={6}
                  style={styles.codeInput}
                />

                {error ? <Text style={[styles.error, { color: '#d93025' }]}>{error}</Text> : null}

                <PrimaryCta title={t('login.verifyCode')} onPress={handleOtpSignIn} loading={submitting} disabled={otp.trim().length < 6} />
                <NativeButton
                  title={
                    otpResendCooldown.isActive
                      ? t('login.resendCodeIn', { seconds: otpResendCooldown.secondsLeft })
                      : t('login.resendCode')
                  }
                  onPress={() => { void sendOtpCode(); }}
                  variant="ghost" size="md" block
                  loading={sendingCode}
                  disabled={otpResendCooldown.isActive}
                />
              </View>
            )}
          </View>
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  accountFoundNotice: {
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 2,
    marginBottom: 4,
  },
  accountFoundTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  accountFoundBody: {
    fontSize: 13,
    lineHeight: 19,
  },
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 50,
  },
  overlaySheet: { justifyContent: 'flex-end' },
  backdrop: { ...StyleSheet.absoluteFillObject },
  sheetWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
    width: '100%',
  },
  sheetWrapSheet: {
    justifyContent: 'flex-end',
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  scrollContent: { flexGrow: 1, justifyContent: 'center' },
  scrollContentSheet: { justifyContent: 'flex-end' },
  scrollFill: { width: '100%' },
  sheet: {
    width: '100%',
    maxWidth: 460,
    alignSelf: 'center',
    borderRadius: 28,
    borderWidth: 0,
    padding: 32,
    backgroundColor: 'var(--menu-bg)',
    boxShadow: 'var(--gem-dialog-shadow)',
  } as any,
  sheetMobile: {
    maxWidth: SHEET_MAX_WIDTH,
    borderTopLeftRadius: SHEET_CORNER_RADIUS,
    borderTopRightRadius: SHEET_CORNER_RADIUS,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingTop: 12,
    paddingHorizontal: 24,
    paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
    boxShadow: 'var(--gem-sheet-shadow)',
  } as any,
  handle: {
    width: 52,
    height: 6,
    borderRadius: 40,
    alignSelf: 'center',
    marginBottom: 8,
    backgroundColor: 'var(--gem-sheet-handle)',
  } as any,
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  section: { gap: 14 },
  title: {
    fontFamily: "'Google Sans', sans-serif",
    fontSize: 28,
    fontWeight: '400',
    lineHeight: 36,
    letterSpacing: '-0.01em',
  } as any,
  subtitle: { fontSize: 15, lineHeight: 22, marginBottom: 4 },
  notice: { fontSize: 13, lineHeight: 20, marginBottom: 12 },
  form: { gap: 12 },
  input: {
    height: 52,
    borderRadius: 9999,
    borderWidth: 1,
    paddingHorizontal: 22,
    paddingVertical: 0,
    fontSize: 15,
    letterSpacing: 0,
    width: '100%',
  },
  codeInput: { letterSpacing: 4, textAlign: 'center', writingDirection: 'ltr', fontSize: 20 },
  preview: { fontSize: 14, fontWeight: '500', marginTop: -4 },
  avatar: {
    width: 96, height: 96, borderRadius: 48, borderWidth: 1,
    alignSelf: 'center', alignItems: 'center', justifyContent: 'center',
    marginVertical: 4, overflow: 'hidden',
  },
  avatarImage: { width: 96, height: 96, borderRadius: 48 },
  avatarPlus: { fontSize: 28, fontWeight: '300', marginTop: -2 },
  avatarLabel: { fontSize: 11, fontWeight: '500', marginTop: 2 },
  error: { fontSize: 13, lineHeight: 18 },
});
